// apps/provider/src/services/scribeUploadService.js
// Prism Scribe direct upload: the browser PUTs recorded audio straight to
// Google Cloud Storage through a single-object resumable session minted by
// Xano (POST /scribe/upload-session). No audio bytes cross an API worker.
//
// The PUT deliberately bypasses apiService.request(): that path injects the
// bearer Authorization header (which a GCS session URI must not receive) and
// routes through the request queue / offline middleware, which cannot replay a
// cross-origin upload. Retry policy: a failed PUT never re-uses its session
// URI (GCS may have persisted a prefix); a fresh session is minted each attempt.

import { apiService } from './api';
import { connectivityMonitor } from './offline/connectivityMonitor';

const MAX_ATTEMPTS = 3;
const BACKOFF_MS = [0, 1500, 4000];

/** Thrown when the session could not be minted (endpoint missing/denied). */
export class ScribeUploadSessionError extends Error {
  constructor(message, cause) {
    super(message);
    this.name = 'ScribeUploadSessionError';
    this.cause = cause;
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function putToSession(sessionUrl, blob, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', sessionUrl, true);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.timeout = 10 * 60 * 1000;

    if (typeof onProgress === 'function' && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress({ loaded: event.loaded, total: event.total, ratio: event.total ? event.loaded / event.total : 0 });
        }
      };
    }

    xhr.onload = () => resolve({ status: xhr.status, body: xhr.responseText });
    xhr.onerror = () => reject(new Error('Audio upload failed (network or CORS error)'));
    xhr.ontimeout = () => reject(new Error('Audio upload timed out'));
    xhr.onabort = () => reject(new Error('Audio upload aborted'));
    xhr.send(blob);
  });
}

/**
 * Upload one recorder session's audio blob to GCS.
 *
 * @param {Blob} blob
 * @param {{ encounterId: string, recordingId: string, segmentIndex: number, onProgress?: Function }} options
 * @returns {Promise<{ bucket: string, name: string, gs_uri: string, content_type: string, size: number, segment_index: number, uploaded_at: string }>}
 * @throws {ScribeUploadSessionError} when the session cannot be minted (callers may fall back)
 * @throws {Error} when every PUT attempt failed
 */
export async function uploadSessionAudio(blob, { encounterId, recordingId, segmentIndex, onProgress } = {}) {
  if (!blob || !blob.size) throw new Error('No audio to upload');
  if (!encounterId || !recordingId) throw new Error('Recording must be saved before its audio can be uploaded');
  if (!connectivityMonitor.isOnline()) throw new Error('You appear to be offline. Audio will be uploaded when the connection returns.');

  // MediaRecorder reports parameterized MIMEs ('audio/webm;codecs=opus');
  // normalize to the base type for the mint, the PUT header, and the stored
  // descriptor (GCS and Gemini both want the bare type).
  const contentType = (blob.type || 'audio/webm').split(';')[0].trim().toLowerCase() || 'audio/webm';
  let lastError = null;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (BACKOFF_MS[attempt]) await sleep(BACKOFF_MS[attempt]);

    let session;
    try {
      session = await apiService.createScribeUploadSession({
        encounter_id: encounterId,
        recording_id: recordingId,
        segment_index: segmentIndex,
        content_type: contentType,
        content_length: blob.size,
        origin: window.location.origin,
      });
    } catch (error) {
      throw new ScribeUploadSessionError(error?.apiMessage || error?.message || 'Upload session unavailable', error);
    }

    if (!session?.session_url) {
      throw new ScribeUploadSessionError('Upload session response was incomplete');
    }

    try {
      const result = await putToSession(session.session_url, blob, contentType, onProgress);
      if (result.status === 200 || result.status === 201) {
        return {
          bucket: session.bucket,
          name: session.object_name,
          gs_uri: session.gs_uri,
          content_type: contentType,
          size: blob.size,
          segment_index: segmentIndex,
          uploaded_at: new Date().toISOString(),
        };
      }
      lastError = new Error(`Audio upload failed (HTTP ${result.status})`);
    } catch (error) {
      lastError = error;
    }
    console.warn(`[ScribeUpload] attempt ${attempt + 1} of ${MAX_ATTEMPTS} failed:`, lastError?.message);
  }

  throw lastError || new Error('Audio upload failed');
}
